import type { ColumnDef } from "@tanstack/react-table"
import type { TFunction } from "i18next"
import {
  ChevronDownIcon,
  ChevronRightIcon,
  CircleCheckIcon,
  DownloadIcon,
  ExternalLinkIcon,
  MoreVerticalIcon,
  Undo2Icon,
  XCircleIcon,
} from "lucide-react"
import React, { useState } from "react"
import { Link } from "react-router-dom"

import { ClipboardButton } from "@/components/commons/clipboard-button"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { FRONTEND_ROUTES } from "@/constants"
import {
  PaymentMethodNameEnum,
  PermissionsRolesEnum,
} from "@/enums"
import { CycleStatusEnum } from "@/enums/subscriptions"
import { getPaymentMethod } from "@/helpers/defaulters/get-payment-method"
import { formatMoney } from "@/helpers/format"
import { useDownloadCancellationLetter } from "@/hooks"
import { DateHelper } from "@/utils"

import { ProcessorInfoPopover } from "../transactions/processor-info-popover"
import { CycleStatusBadge } from "./cycle-status-badge"
import { RefundSubscriptionDialog } from "./refund-subscription-dialog"
import { ReverseCycleDialog } from "./reverse-cycle-dialog"
import {
  canReverseCycle,
  isCancelPaymentMethodByType,
} from "./SubscriptionsHelper"

const SUBSCRIPTION_TYPE_VTEX = "VTEX"

export const createSubscriptionsDetailsColumns = (
  t: TFunction,
  hasPermission: (
    permissions: PermissionsRolesEnum[]
  ) => boolean
): ColumnDef<SubscriptionDetailsColumnsType>[] => [
  {
    id: "expander",
    header: () => null,
    size: 28,
    enableHiding: false,
    cell: ({ row }) => {
      return row.getCanExpand() ? (
        <Button
          {...{
            className:
              "size-7 shadow-none text-muted-foreground",
            onClick: row.getToggleExpandedHandler(),
            "aria-expanded": row.getIsExpanded(),
            "aria-label": row.getIsExpanded()
              ? `Collapse details for ${row.original.code}`
              : `Expand details for ${row.original.code}`,
            size: "icon",
            variant: "ghost",
          }}
        >
          {row.getIsExpanded() ? (
            <ChevronDownIcon
              className="opacity-60"
              size={16}
              strokeWidth={2}
              aria-hidden="true"
            />
          ) : (
            <ChevronRightIcon
              className="opacity-60"
              size={16}
              strokeWidth={2}
              aria-hidden="true"
            />
          )}
        </Button>
      ) : undefined
    },
  },
  {
    header: t("subscriptions.table.cycle"),
    accessorKey: "billingCycle",
    size: 28,
    cell: ({ row }) => {
      return (
        <label
          className="text-md flex size-4 cursor-pointer items-center justify-center rounded-full p-2.5 font-bold"
          onClick={row.getToggleExpandedHandler()}
        >
          {row.original.billingCycle}
        </label>
      )
    },
  },
  {
    header: t("subscriptions.table.dates"),
    accessorKey: "dates",
    cell: ({ row }) => {
      const { original } = row
      return (
        <div
          className="flex cursor-pointer flex-col gap-1"
          onClick={row.getToggleExpandedHandler()}
        >
          {original?.processingDate && (
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-muted-foreground">
                {t("subscriptions.table.processingDate")}:
              </span>
              <span className="text-xs">
                {DateHelper.formatDate(
                  original.processingDate,
                  "dd/MM/yyyy"
                )}
              </span>
            </div>
          )}
          {original?.dueDate && (
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-muted-foreground">
                {t("subscriptions.table.dueDate")}:
              </span>
              <span className="text-xs">
                {DateHelper.formatDate(
                  original.dueDate,
                  "dd/MM/yyyy"
                )}
              </span>
            </div>
          )}
        </div>
      )
    },
  },
  {
    header: t("subscriptions.table.status"),
    accessorKey: "status",
    cell: ({ row }) => {
      const { original } = row
      return (
        <CycleStatusBadge
          status={original.status as CycleStatusEnum}
          paymentMethod={
            original.paymentInstrument
              ?.type as PaymentMethodNameEnum
          }
        />
      )
    },
  },
  {
    header: t("subscriptions.table.amount"),
    accessorKey: "totalAmount",
    cell: ({ row }) => {
      const { original } = row
      const amount = original?.amount || 0
      const discount = original?.discount || 0
      const interest = original?.interest || 0
      const totalAmount = original?.totalAmount || 0

      return (
        <div
          className="flex cursor-pointer flex-col gap-1"
          onClick={row.getToggleExpandedHandler()}
        >
          <span className="text-md font-semibold">
            {formatMoney(totalAmount / 100)}
          </span>
          <div className="flex flex-col gap-0.5 text-[9px] text-muted-foreground">
            <span>
              {t("common.baseAmount")}:{" "}
              {formatMoney(amount / 100)}
            </span>
            {discount > 0 && (
              <span>
                {t("common.discount")}: -
                {formatMoney(discount / 100)}
              </span>
            )}
            {interest > 0 && (
              <span>
                {t("common.interest")}: +
                {formatMoney(interest / 100)}
              </span>
            )}
          </div>
        </div>
      )
    },
  },
  {
    header: t("subscriptions.table.paymentMethod"),
    accessorKey: "paymentMethod",
    cell: ({ row }) => {
      const { original } = row
      const paymentInstrument = original?.paymentInstrument
      const paymentMethodName = getPaymentMethod(
        paymentInstrument?.type,
        t
      )
      const isCreditCard =
        paymentInstrument?.type ===
          PaymentMethodNameEnum.CREDIT_CARD &&
        paymentInstrument?.card
      const logoUrl =
        original?.paymentInstrument?.card?.brand?.logoUrl
      const lastFourDigits =
        original?.paymentInstrument?.card?.lastFourDigits
      return (
        <div
          key={paymentInstrument?.id}
          className="flex cursor-pointer flex-col"
          onClick={row.getToggleExpandedHandler()}
        >
          <label>{paymentMethodName}</label>
          {isCreditCard && (
            <div className="flex items-center gap-1 font-semibold">
              <img
                src={logoUrl}
                alt={paymentInstrument?.id}
                width={36}
                height={36}
              />
              <span>Final: {lastFourDigits}</span>
            </div>
          )}
        </div>
      )
    },
  },
  {
    header: t("orders.transactionsTable.processor"),
    accessorKey: "processorInformation",
    cell: ({ row }) => {
      const { original } = row
      const processorInfo = original
        ?.paymentInstruments?.[0]?.processorInformation as
        | TransactionsDetailsColumnsType["processorInformation"]
        | undefined
      return (
        <div
          onClick={row.getToggleExpandedHandler()}
          className="cursor-pointer"
        >
          <ProcessorInfoPopover
            processorInfo={processorInfo}
            t={t}
          />
        </div>
      )
    },
  },
  {
    header: t("subscriptions.table.paymentId"),
    accessorKey: "paymentId",
    cell: ({ row }) => {
      const paymentId = row.original?.paymentId
      if (!paymentId) return <span>-</span>
      return (
        <div className="flex items-center gap-1">
          <code
            className="max-w-[120px] cursor-pointer truncate rounded bg-muted px-1.5 py-0.5 text-xs"
            title={paymentId}
            onClick={row.getToggleExpandedHandler()}
          >
            {paymentId}
          </code>
          <ClipboardButton
            text={paymentId}
            variant="ghost"
            className="h-6 w-6"
          />
        </div>
      )
    },
  },
  {
    header: t("subscriptions.table.action"),
    accessorKey: "action",
    cell: ({ row }) => {
      const { original } = row
      const paymentInstrument = original?.paymentInstrument

      const status = original?.order?.statusDetail
        ?.status as CycleStatusEnum
      const subscriptionType = original.subscriptionType
      const subscriptionId = original?.subscriptionId
      const billingCycle = original?.billingCycle
      const paymentId = original?.paymentId
      const paymentMethod =
        paymentInstrument?.type as PaymentMethodNameEnum
      const orderId = original?.orderId

      const isVTEX =
        subscriptionType === SUBSCRIPTION_TYPE_VTEX
      const canRefund = hasPermission([
        PermissionsRolesEnum.SUBSCRIPTIONS_WRITE,
      ])
      const isRefundable =
        ![
          CycleStatusEnum.PROCESSING_PAYMENT,
          CycleStatusEnum.REFUNDED,
          CycleStatusEnum.PROCESSING_REFUNDING,
          CycleStatusEnum.REVERSED,
        ].includes(status) &&
        !isVTEX &&
        !!paymentId &&
        canRefund

      const isCancelPaymentMethod =
        isCancelPaymentMethodByType(paymentMethod)

      const isCancellable =
        status === CycleStatusEnum.REFUNDED &&
        paymentMethod ===
          PaymentMethodNameEnum.CREDIT_CARD &&
        !isVTEX

      const refundLabel = isCancelPaymentMethod
        ? t("common.cancel")
        : t("common.refund")
      const RefundIcon = isCancelPaymentMethod
        ? XCircleIcon
        : CircleCheckIcon

      const isReversible = canReverseCycle(
        {
          id: original?.id,
          subscriptionId,
          orderSubscriptionId:
            original?.orderSubscriptionId,
          subscriptionType,
          paymentId,
          status: original?.statusDetail?.status,
          paymentMethod,
        },
        canRefund
      )

      return (
        <ActionsDropdown
          cycleId={original?.id}
          isReversible={isReversible}
          orderId={orderId}
          receiptUrl={original?.order?.receiptUrls?.pdf}
          isRefundable={isRefundable}
          isCancellable={isCancellable}
          refundLabel={refundLabel}
          RefundIcon={RefundIcon}
          subscriptionId={subscriptionId}
          billingCycle={billingCycle}
          paymentMethod={paymentMethod}
          t={t}
        />
      )
    },
  },
]

type ActionsDropdownProps = {
  cycleId?: string
  isReversible: boolean
  orderId?: string
  receiptUrl?: string
  isRefundable: boolean
  isCancellable: boolean
  refundLabel: string
  RefundIcon: React.ElementType
  subscriptionId?: string
  billingCycle: number
  paymentMethod?: PaymentMethodNameEnum
  t: TFunction
}

const ActionsDropdown: React.FC<ActionsDropdownProps> = ({
  cycleId,
  isReversible,
  orderId,
  receiptUrl,
  isRefundable,
  isCancellable,
  refundLabel,
  RefundIcon,
  subscriptionId,
  billingCycle,
  paymentMethod,
  t,
}) => {
  const [showRefundDialog, setShowRefundDialog] =
    useState(false)
  const [showReversalDialog, setShowReversalDialog] =
    useState(false)
  const { handleDownload, isDownloading } =
    useDownloadCancellationLetter({
      subscriptionId,
      billingCycle,
    })

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            className="h-8 w-8 p-0"
          >
            <MoreVerticalIcon className="h-4 w-4" />
            <span className="sr-only">
              {t("common.actions")}
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-[200px]"
        >
          <DropdownMenuLabel>
            {t("common.actions")}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {orderId && (
            <DropdownMenuItem asChild>
              <Link
                to={`${FRONTEND_ROUTES.ORDERS.href}/details/${orderId}`}
                target="_blank"
                className="flex cursor-pointer items-center gap-2"
              >
                <ExternalLinkIcon size={14} />
                <span>{t("common.orderDetails")}</span>
              </Link>
            </DropdownMenuItem>
          )}
          {receiptUrl && (
            <DropdownMenuItem
              onClick={() =>
                window.open(receiptUrl, "_blank")
              }
              className="flex cursor-pointer items-center gap-2"
            >
              <DownloadIcon size={14} />
              <span>{t("common.receipt")}</span>
            </DropdownMenuItem>
          )}
          {isRefundable && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation()
                setShowRefundDialog(true)
              }}
              className="flex cursor-pointer items-center gap-2 text-destructive focus:text-destructive"
            >
              <RefundIcon size={14} />
              <span>{refundLabel}</span>
            </DropdownMenuItem>
          )}
          {isReversible && (
            <DropdownMenuItem
              className="flex cursor-pointer items-center gap-2 text-destructive focus:text-destructive"
              onClick={(e) => {
                e.stopPropagation()
                setShowReversalDialog(true)
              }}
            >
              <Undo2Icon size={14} />
              <span>
                {t("subscriptions.cycleReversal.button")}
              </span>
            </DropdownMenuItem>
          )}
          {isCancellable && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation()
                handleDownload()
              }}
              disabled={isDownloading}
              className="flex cursor-pointer items-center gap-2"
            >
              <DownloadIcon size={14} />
              <span>
                {t(
                  "subscriptions.actions.cancellationLetter"
                )}
              </span>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {showRefundDialog && subscriptionId && (
        <RefundSubscriptionDialog
          id={subscriptionId}
          billingCycle={billingCycle}
          open={showRefundDialog}
          setOpen={setShowRefundDialog}
          paymentMethod={paymentMethod}
        />
      )}
      {showReversalDialog && subscriptionId && cycleId && (
        <ReverseCycleDialog
          cycleId={cycleId}
          open={showReversalDialog}
          setOpen={setShowReversalDialog}
          subscriptionId={subscriptionId}
        />
      )}
    </>
  )
}
